"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";

// Routes that own the full viewport (full-screen app surfaces).
// On these, the marketing Footer would push the document below the
// fold and turn into a scroll-trap -- the user finishes a chat
// message, scrolls one click too far, and lands in the privacy
// policy. /maren is the chief offender (chat tries to fill 100svh
// minus nav). Add more pure-app routes here as we identify them.
const FULL_BLEED_ROUTES = ["/maren"];

export default function Footer() {
  const pathname = usePathname();
  if (FULL_BLEED_ROUTES.some((r) => pathname === r || pathname.startsWith(`${r}/`))) {
    return null;
  }
  const year = new Date().getFullYear();
  return (
    <footer className="border-t border-maren-sage/40 dark:border-maren-sage/20 mt-12 bg-maren-stone dark:bg-maren-night">
      <div className="max-w-6xl mx-auto px-6 py-10 space-y-5 text-xs text-maren-charcoal/70 dark:text-maren-stone/70">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="space-y-2">
            {/* Footer brand block: brand-color logo tile + Cormorant
                "Maren" + tagline. The tile is the same PNG used in Nav;
                its forest-green canvas is baked in (no transparency)
                so it reads as a brand mark on both stone and night
                surfaces. Subtle sage ring in dark mode for definition. */}
            <div className="flex items-center gap-3">
              <img
                src="/brand/maren-logo-128.png"
                alt=""
                aria-hidden="true"
                width={128}
                height={128}
                className="h-12 w-12 rounded-lg object-cover shadow-sm ring-1 ring-maren-charcoal/0 dark:ring-maren-sage/15"
              />
              <div className="flex flex-col">
                <span className="font-display text-2xl font-medium leading-none text-maren-green dark:text-maren-stone">
                  Maren
                </span>
                {/* Review-first brand line (2026-07-01): replaces "The
                    tutor that knows you." per the no-AI/no-tutor brand
                    direction for outward surfaces. DRAFT, wording review. */}
                <span className="mt-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-maren-charcoal/55 dark:text-maren-stone/55">
                  Turn missed questions into a plan.
                </span>
              </div>
            </div>
            <p className="font-medium text-maren-charcoal dark:text-maren-stone pt-3">
              © {year} LIRA Study LLC. Maren™ is a product of LIRA Study LLC.
            </p>
            {/*
              No physical postal address shown on the website yet.
              CAN-SPAM (15 U.S.C. §7704(a)(5)) requires the address in
              every commercial email message, NOT on the website itself,
              so a pre-launch site that doesn't yet send marketing email
              is compliant without one. Founder safety / personal-privacy
              choice: keep the residential address off the public web
              indefinitely. Before the first NCLEX-launch email send,
              wire a virtual-mailbox or registered-agent address into both
              the email template footer AND the Privacy Policy §12 block,
              and re-add it here for consistency. Tracked in
              legal-review-20260422.md C2.
            */}
          </div>
          <nav className="flex flex-wrap gap-x-5 gap-y-2 font-medium">
            <Link
              href="/terms"
              className="text-maren-charcoal/70 dark:text-maren-stone/70 hover:text-maren-green dark:hover:text-maren-gold transition"
            >
              Terms
            </Link>
            <Link
              href="/privacy"
              className="text-maren-charcoal/70 dark:text-maren-stone/70 hover:text-maren-green dark:hover:text-maren-gold transition"
            >
              Privacy
            </Link>
            <Link
              href="/disclaimer"
              className="text-maren-charcoal/70 dark:text-maren-stone/70 hover:text-maren-green dark:hover:text-maren-gold transition"
            >
              Disclaimer
            </Link>
            <Link
              href="/refunds"
              className="text-maren-charcoal/70 dark:text-maren-stone/70 hover:text-maren-green dark:hover:text-maren-gold transition"
            >
              Refunds
            </Link>
            <Link
              href="/security"
              className="text-maren-charcoal/70 dark:text-maren-stone/70 hover:text-maren-green dark:hover:text-maren-gold transition"
            >
              Security
            </Link>
            <Link
              href="/accessibility"
              className="text-maren-charcoal/70 dark:text-maren-stone/70 hover:text-maren-green dark:hover:text-maren-gold transition"
            >
              Accessibility
            </Link>
            <Link
              href="/help"
              className="text-maren-charcoal/70 dark:text-maren-stone/70 hover:text-maren-green dark:hover:text-maren-gold transition"
            >
              Help
            </Link>
            <Link
              href="/creators"
              className="text-maren-charcoal/70 dark:text-maren-stone/70 hover:text-maren-green dark:hover:text-maren-gold transition"
            >
              Creators
            </Link>
            <a
              href="mailto:support@joinmaren.com"
              className="text-maren-charcoal/70 dark:text-maren-stone/70 hover:text-maren-green dark:hover:text-maren-gold transition"
            >
              Contact
            </a>
          </nav>
        </div>

        {/* Terse footer notice. The full trademark + nominative-fair-use text
            lives on /disclaimer so it's still linked-and-findable -- it
            doesn't dominate every page footer with competitor names. */}
        <div className="pt-5 border-t border-maren-sage/30 dark:border-maren-sage/20 leading-relaxed">
          <p>
            Maren is an independent study tool from LIRA Study LLC -- not affiliated with any test-prep provider,
            medical school, or licensing body. MCAT<sup>®</sup> is a registered trademark of the AAMC;
            other marks belong to their respective owners. See the{" "}
            <Link
              href="/disclaimer"
              className="underline underline-offset-2 hover:text-maren-green dark:hover:text-maren-gold transition"
            >
              full disclaimer
            </Link>
            {" "}for details. Maren is AI-assisted; content may contain errors and is not a substitute
            for professional tutoring, medical, legal, or admissions advice.
          </p>
        </div>
      </div>
    </footer>
  );
}
